import { useRef, useState, useEffect } from 'react';
import { TSuggestions, TUseSuggestionsParams } from '../../../../../types/dealerLocatorLanding.types';

const DL_BY_DEALER_NAME_API_URL = '/apirequest/dealer-locator/get-dealer-by-dealer-name';
const DEBOUNCE_DELAY = 500;
const MIN_SEARCH_TERM_LENGHT = 3;

const useSuggestions = (
  query: string,
  inputText: string,
  autocompleteService: google.maps.places.AutocompleteService,
  { pageId, language, countryIsoCode, resultsLimit }: TUseSuggestionsParams
): [TSuggestions] => {
  const [suggestions, setSuggestions] = useState<TSuggestions | null>(null);
  const controllerRef = useRef<AbortController | null>();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const debounceFn = (callback) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(callback, DEBOUNCE_DELAY);
  };

  const getComponnentsParam = (): { country: Array<string> } | undefined => {
    if (!countryIsoCode) {
      return;
    }

    return {
      country: Array.isArray(countryIsoCode) ? countryIsoCode : [countryIsoCode],
    };
  };

  const fetchPlacesFromGoogle = () => {
    const req = {
      input: query,
      types: ['geocode'],
      componentRestrictions: getComponnentsParam(),
    };

    return new Promise<google.maps.places.AutocompletePrediction[] | null>((resolve, reject) => {
      autocompleteService.getPlacePredictions(req, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(results);
        } else {
          reject('Failed to fetch place detail from Google Places API');
        }
      });
    });
  };

  const fetchDalersFromDealerPortal = () => {
    const queryUrlParams = new URLSearchParams({
      pageId: pageId,
      language: language,
      dealerName: query,
      country: Array.isArray(countryIsoCode) ? `${countryIsoCode.join('|')}` : countryIsoCode,
    });

    const queyrUrl = `${DL_BY_DEALER_NAME_API_URL}?${queryUrlParams}`;

    return fetch(queyrUrl, { signal: controllerRef.current?.signal }).then((res) => {
      if (res.ok) {
        return res.json();
      } else {
        console.error(`${res.status} : Failed to fetch dealers results.`);
      }
    });
  };

  const fetchSuggestions = (): void => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    if (query?.length < MIN_SEARCH_TERM_LENGHT) {
      setSuggestions(null);
      return;
    }

    Promise.allSettled([fetchPlacesFromGoogle(), fetchDalersFromDealerPortal()]).then((promiseResults) => {
      const [googlePlacesResuls, dealersResult] = promiseResults;
      const data: TSuggestions = { googlePlacesPredictions: [], dealershipResults: [] };
      let predictionsResults: google.maps.places.AutocompletePrediction[] = [];
      let dealershipResults = [];

      if (googlePlacesResuls?.status === 'fulfilled') {
        predictionsResults = googlePlacesResuls?.value || [];
      }

      if (dealersResult?.status === 'fulfilled') {
        dealershipResults = dealersResult?.value?.dealershipResults;
      }

      const groupLimit = resultsLimit / 2;
      const dealershipResultsCount = dealershipResults?.length || 0;
      const predictionsResultsCount = predictionsResults?.length || 0;
      const predictionsResultsLimit = dealershipResultsCount < groupLimit ? resultsLimit - dealershipResultsCount : groupLimit;
      const dealershipResultsLimit = predictionsResultsCount < groupLimit ? resultsLimit - predictionsResultsCount : groupLimit;

      data['googlePlacesPredictions'] = predictionsResults?.slice(0, predictionsResultsLimit) || [];
      data['dealershipResults'] = dealershipResults?.slice(0, dealershipResultsLimit) || [];

      setSuggestions(data);
    });
  };

  useEffect(() => {
    try {
      if (inputText !== query) {
        setSuggestions(null);
        return;
      }

      debounceFn(() => fetchSuggestions());
    } catch (e) {
      setSuggestions(null);
    }
  }, [query, inputText]);

  return [suggestions];
};

export default useSuggestions;
